import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Card from '../components/ui/Card';
import { Callout } from '../components/buyer-report/Callout';

/**
 * Sample property data — fictional, mirrors the live v2 Buyer's Report
 * structure. Designed to exercise the "mixed" risk-flag scenario: one
 * higher-attention item, two worth-checking items, two for-information
 * items. Listed-building + conservation-area + Article 4 are the
 * heritage/alteration triggers; Council Tax + flood-zone-2 round it out.
 */
const SAMPLE = {
  address: { line1: '42 Oakfield Road', city: 'Clifton, Bristol', postcode: 'BS8 2BJ', uprn: '10001234567' },
  generatedAt: '2026-02-15T10:30:00Z',
  paidAt: '2026-02-15',
  reportId: 'BR-7K3M2N4P',
  localAuthority: 'Bristol City Council',
};

const FIELD_MAPPING_EXAMPLES = [
  { document: 'Title register', field: 'TA6 Section 1.1', label: 'Property address', example: '42 Oakfield Road, Clifton, Bristol BS8 2BJ' },
  { document: 'Title register', field: 'TA6 Section 1.2', label: 'Tenure', example: 'Leasehold — 125 years from 1 January 2005' },
  { document: 'Lease agreement', field: 'TA7 Section 1.1', label: 'Lease length remaining', example: '104 years remaining' },
  { document: 'Lease agreement', field: 'TA7 Section 1.3', label: 'Ground rent', example: '£250 per annum, reviewed every 25 years' },
  { document: 'Service charge statement', field: 'LPE1 Section 2.1', label: 'Annual service charge', example: '£1,850 per annum (2025/26)' },
  { document: 'Buildings insurance', field: 'LPE1 Section 3.1', label: 'Insurance provider', example: 'Aviva — policy expires 31 March 2027' },
];

const DOCUMENT_CHECKLIST = [
  { name: 'Title register', why: 'Property address, tenure, ownership details', required: true },
  { name: 'Title plan', why: 'Property boundaries and extent', required: true },
  { name: 'Lease agreement', why: 'Lease terms, ground rent, restrictions (leasehold only)', required: false },
  { name: 'Service charge statement', why: 'Annual charges, management company details', required: false },
  { name: 'Buildings insurance certificate', why: 'Insurance cover, provider, expiry date', required: false },
  { name: 'Planning permission documents', why: 'Approved works, conditions, completion certificates', required: false },
  { name: 'Building regulations certificates', why: 'Compliance for alterations and extensions', required: false },
  { name: 'FENSA/Gas Safe certificates', why: 'Window replacements, boiler installations', required: false },
];

export default function Samples() {
  return (
    <>
      <Helmet>
        <title>Sample Reports | UKPropertyChecks</title>
        <meta name="description" content="See sample Buyer's Reports and Seller's Packs before you buy." />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/samples" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Sample reports — UKPropertyChecks",
            description: "Fictional sample Buyer's Report and Seller's Pack showing the structure and content of UKPropertyChecks outputs.",
            url: "https://ukpropertychecks.co.uk/samples",
            about: [
              {
                "@type": "Product",
                name: "Buyer's Report",
                description: "Pre-offer due-diligence summary covering EPC, flood, heritage, alterations, planning history, and tax/regulatory rules. Sample uses fictional data.",
                offers: {
                  "@type": "Offer",
                  priceCurrency: "GBP",
                  price: "29.00",
                  url: "https://ukpropertychecks.co.uk/",
                },
              },
              {
                "@type": "Product",
                name: "Seller's Pack",
                description: "Solicitor-ready TA6, TA7, and LPE1 forms for a UK property sale. Sample shows field-to-document mapping.",
                offers: {
                  "@type": "Offer",
                  priceCurrency: "GBP",
                  price: "79.00",
                  url: "https://ukpropertychecks.co.uk/selling-documents",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen">
        <div className="container-main section-spacing">
          <h1>Sample reports</h1>
          <p className="mt-3 text-slate-600">
            See what you receive before entering your property details.
          </p>

          {/* ─── BUYER'S REPORT SAMPLE ─── */}
          {/*
            Mirrors the live v2 8-section information architecture:
              1. Headline (address + risk-flag buckets)
              2. What this report covers
              3. EPC
              4. Flood risk
              5. Heritage and conservation
              6. Planning constraints and rights
              7. Planning application history
              8. Tax and regulatory rules
            Title summary is omitted (no HMLR yet) and acknowledged in §2.
            Sample data is fictional but exercises a "mixed" risk profile.
          */}
          <section className="mt-10">
            <h2 className="text-2xl font-semibold text-slate-950">Buyer's Report</h2>
            <p className="mt-2 text-sm text-slate-600">
              A factual summary of public records covering EPC, flood risk, heritage, alteration constraints, planning history, and tax rules. £29 per report.
            </p>

            <Card variant="subtle" className="mt-6 border-l-4 border-amber-400 bg-amber-50">
              <p className="text-sm text-amber-900">
                Sample using fictional data. Your report will contain data specific to your property. Title summary is not yet included &mdash; we'll add it once HM Land Registry integration is live.
              </p>
            </Card>

            <div className="mt-6 space-y-6">

              {/* §1 — Headline */}
              <Card>
                <h1 className="text-3xl font-bold text-slate-950 mb-1">Buyer's Report</h1>
                <div className="text-xl font-semibold text-slate-950 mt-4 leading-tight">
                  <div>{SAMPLE.address.line1}</div>
                  <div>{SAMPLE.address.city}</div>
                  <div>{SAMPLE.address.postcode}</div>
                </div>
                <dl className="mt-4 space-y-1 text-sm text-slate-700">
                  <div>Generated 15 February 2026 at 10:30</div>
                  <div>Report ID: <span className="font-mono text-xs">{SAMPLE.reportId}</span></div>
                  <div>UPRN: <span className="font-mono text-xs">{SAMPLE.address.uprn}</span></div>
                  <div>Local planning authority: {SAMPLE.localAuthority}</div>
                  <div>Paid on 15 February 2026</div>
                </dl>

                <hr className="my-6 border-slate-200" />

                <h2 className="text-lg font-semibold text-slate-950 mb-3">Findings at a glance</h2>
                <p className="text-sm text-slate-700 mb-4">
                  Risk flags grouped by attention level. Items in &ldquo;Higher attention&rdquo; should be discussed with your solicitor before exchange.
                </p>

                <div className="space-y-3">
                  <Callout severity="high" heading="Higher attention (1)">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Listed building (Grade II) &mdash; Listed Building Consent required for most works.</li>
                    </ul>
                  </Callout>
                  <Callout severity="warning" heading="Worth checking (2)">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Within Clifton and Hotwells Conservation Area &mdash; permitted development restricted.</li>
                      <li>Article 4 Direction in force &mdash; HMO conversions need formal application.</li>
                    </ul>
                  </Callout>
                  <Callout severity="info" heading="For information (2)">
                    2 items recorded for information. See the relevant sections below.
                  </Callout>
                </div>
              </Card>

              {/* §2 — What this report covers */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">What does this report cover?</h3>
                <p className="text-sm text-slate-800 mb-4">
                  This report draws on public records held by named UK government and statutory bodies. Where a section returns no matching records, we record that as &ldquo;No&rdquo; rather than absence of information. Sources do not always reflect very recent changes.
                </p>

                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Topic</th>
                        <th className="text-left px-4 py-2 font-semibold">Source</th>
                        <th className="text-left px-4 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Energy Performance Certificate</td><td className="px-4 py-2 text-slate-700">EPC Register (Open Data Communities)</td><td className="px-4 py-2">Checked</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Flood risk</td><td className="px-4 py-2 text-slate-700">Environment Agency</td><td className="px-4 py-2">Checked</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Heritage and conservation</td><td className="px-4 py-2 text-slate-700">Historic England (NHLE) + planning.data.gov.uk</td><td className="px-4 py-2">Checked</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Planning constraints and rights</td><td className="px-4 py-2 text-slate-700">planning.data.gov.uk</td><td className="px-4 py-2">Checked</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Planning application history</td><td className="px-4 py-2 text-slate-700">planning.data.gov.uk</td><td className="px-4 py-2">Checked</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Tax and regulatory rules</td><td className="px-4 py-2 text-slate-700">UK statutory rules</td><td className="px-4 py-2">Checked</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Title summary</td><td className="px-4 py-2 text-slate-700">HM Land Registry</td><td className="px-4 py-2 text-slate-600">Not yet checked &mdash; planned</td></tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-slate-600 mt-4">
                  A &ldquo;Title summary&rdquo; section (charges, restrictions, lease term remaining) is planned but is not yet checked because we have not integrated HM Land Registry. Your solicitor will obtain the current title register as part of conveyancing.
                </p>
              </Card>

              {/* §3 — EPC */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">How energy-efficient is the property?</h3>
                <p className="text-sm text-slate-800 mb-4">
                  An Energy Performance Certificate (EPC) rates a property from A (most efficient) to G (least efficient). It is required when a property is sold or let. The figures below are taken from the most recent certificate on the public register.
                </p>

                <div className="flex items-center gap-6 mb-6">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center text-2xl font-bold text-slate-950 bg-yellow-400">D</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Current rating: D (58/100)</p>
                    <p className="text-sm text-slate-600">Potential rating: B (82/100)</p>
                  </div>
                </div>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Certificate detail</h4>
                <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2 font-semibold text-slate-950">Certificate date</td><td className="px-4 py-2 text-slate-800">12 March 2023</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2 font-semibold text-slate-950">Property type</td><td className="px-4 py-2 text-slate-800">Mid-terrace house</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2 font-semibold text-slate-950">Total floor area</td><td className="px-4 py-2 text-slate-800">95 m²</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2 font-semibold text-slate-950">Expires</td><td className="px-4 py-2 text-slate-800">12 March 2033</td></tr>
                    </tbody>
                  </table>
                </div>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Recommended improvements</h4>
                <p className="text-sm text-slate-700 mb-3">
                  Suggestions taken from the most recent certificate. Costs and savings are indicative; the actual figures depend on the property and current energy prices.
                </p>
                <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Measure</th>
                        <th className="text-left px-4 py-2 font-semibold">Indicative cost</th>
                        <th className="text-left px-4 py-2 font-semibold">Annual saving</th>
                        <th className="text-left px-4 py-2 font-semibold">Projected rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Increase loft insulation to 270 mm</td><td className="px-4 py-2 text-slate-700">£100&ndash;£350</td><td className="px-4 py-2 text-slate-700">£35</td><td className="px-4 py-2">C (62)</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Internal or external wall insulation</td><td className="px-4 py-2 text-slate-700">£4,000&ndash;£14,000</td><td className="px-4 py-2 text-slate-700">£420</td><td className="px-4 py-2">B (78)</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Solar photovoltaic panels (2.5 kWp)</td><td className="px-4 py-2 text-slate-700">£3,500&ndash;£5,500</td><td className="px-4 py-2 text-slate-700">£195</td><td className="px-4 py-2">B (82)</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-600">
                  Source: Energy Performance of Buildings Register &mdash; Open Data Communities (Open Government Licence v3.0).
                </p>
              </Card>

              {/* §4 — Flood risk */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">What is the flood risk?</h3>
                <p className="text-sm text-slate-800 mb-4">
                  Flood risk is recorded by the Environment Agency under two headings: rivers and sea (which is what mortgage lenders look at), and surface water (rainfall that pools or runs off). Both are summarised below.
                </p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Rivers and sea</h4>
                <p className="text-sm text-slate-800 mb-1"><span className="font-semibold">Zone 1 &mdash; low risk</span></p>
                <p className="text-sm text-slate-700 mb-4">Less than 1-in-1,000 annual probability of flooding from rivers or the sea.</p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Surface water</h4>
                <p className="text-sm text-slate-800 mb-1"><span className="font-semibold">Low risk</span></p>
                <p className="text-sm text-slate-700 mb-4">Between 1-in-100 and 1-in-1,000 annual chance of surface-water flooding.</p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Historical flood events</h4>
                <p className="text-sm text-slate-700 mb-4">No historical flood events recorded for this location.</p>

                <p className="text-xs text-slate-600">
                  Source: Environment Agency datasets via planning.data.gov.uk (Open Government Licence v3.0).
                </p>
              </Card>

              {/* §5 — Heritage */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">Is the property protected by heritage designations?</h3>
                <p className="text-sm text-slate-800 mb-4">
                  A heritage designation is a formal listing recognising a building, area or feature as having historic or architectural value. Designations bring extra planning controls &mdash; what you can change, and what consent you need before changing it.
                </p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Statutory designations</h4>
                <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Designation</th>
                        <th className="text-left px-4 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Listed building</td><td className="px-4 py-2">Yes (Grade II)</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Scheduled monument</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Registered park or garden</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Registered battlefield</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Protected wreck site</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">World Heritage Site</td><td className="px-4 py-2">No</td></tr>
                    </tbody>
                  </table>
                </div>

                <Callout severity="high" heading="Listed building (Grade II) — what this means" className="mb-4">
                  <p>Grade II is the most common listing, covering buildings of special interest. Listed Building Consent is required for any work that affects the building's character, including some internal alterations. Carrying out works without consent is a criminal offence. You should expect a longer planning timeline and stricter scrutiny of materials, windows, and external changes.</p>
                </Callout>

                <h4 className="text-sm font-semibold text-slate-950 mt-6 mb-2">Conservation area</h4>
                <p className="text-sm text-slate-800 mb-4">
                  This property is within the Clifton and Hotwells conservation area.
                </p>
                <Callout severity="warning" heading="Conservation area — what this means">
                  <p>Inside a conservation area, permitted development rights (the changes you can make without applying for planning permission) are reduced. Demolition, tree work, satellite dishes, cladding, and some external alterations need consent. Replacement windows often have to match the original style and materials.</p>
                </Callout>

                <p className="text-xs text-slate-600 mt-4">
                  Source: Historic England &mdash; National Heritage List for England (NHLE). Conservation area boundaries from planning.data.gov.uk (Open Government Licence v3.0).
                </p>
              </Card>

              {/* §6 — Planning constraints and rights */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">Can the property be altered or extended?</h3>
                <p className="text-sm text-slate-800 mb-4">
                  Planning constraints are formal designations that affect what can be built, demolished or altered. The six checks below come from public planning datasets. A &ldquo;No&rdquo; means no matching record was found in the dataset; it does not always mean the constraint definitely does not apply.
                </p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Permitted development rights</h4>
                <p className="text-sm text-slate-800 mb-4">
                  Permitted development rights are restricted by an Article 4 Direction (see below).
                </p>

                <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Constraint</th>
                        <th className="text-left px-4 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Green Belt</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Area of Outstanding Natural Beauty (AONB)</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">National Park</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Tree Preservation Order (TPO)</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Site of Special Scientific Interest (SSSI)</td><td className="px-4 py-2">No</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Article 4 Direction</td><td className="px-4 py-2">Yes</td></tr>
                    </tbody>
                  </table>
                </div>

                <Callout severity="warning" heading="Article 4 Direction — what this means">
                  <p>An Article 4 Direction is a council order that removes some of the automatic permitted development rights. Works that would normally not need planning permission &mdash; replacing windows, cladding, satellite dishes, sometimes change of use &mdash; may require a formal application.</p>
                </Callout>

                <p className="text-xs text-slate-600 mt-4">
                  Source: planning.data.gov.uk &mdash; Open Government Licence v3.0. Coverage may be incomplete; check with the local planning authority for the full picture.
                </p>
              </Card>

              {/* §7 — Planning application history */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">What planning history is recorded for this property?</h3>
                <p className="text-sm text-slate-800 mb-4">
                  Planning applications recorded on planning.data.gov.uk for the address or its immediate vicinity. Coverage on this national platform is incomplete &mdash; many councils have not yet published their full back-catalogue, so this is a starting point rather than a full history.
                </p>

                <Callout severity="warning" heading="Coverage caveat" className="mb-4">
                  <p>planning.data.gov.uk receives applications from councils that have onboarded to the platform. If a council has not yet onboarded, or has only recently done so, older applications will not appear here. The local authority's own planning portal is the authoritative source.</p>
                </Callout>

                <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Reference</th>
                        <th className="text-left px-4 py-2 font-semibold">Decision</th>
                        <th className="text-left px-4 py-2 font-semibold">Decision date</th>
                        <th className="text-left px-4 py-2 font-semibold">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">23/04521/F</td><td className="px-4 py-2">Granted</td><td className="px-4 py-2">15 January 2024</td><td className="px-4 py-2 text-slate-700">Rear single-storey extension</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">21/01234/LA</td><td className="px-4 py-2">Granted</td><td className="px-4 py-2">20 September 2021</td><td className="px-4 py-2 text-slate-700">Listed building consent for internal alterations</td></tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-slate-600">
                  Source: planning.data.gov.uk &mdash; Open Government Licence v3.0. Coverage may be incomplete.
                </p>
              </Card>

              {/* §8 — Tax and regulatory rules */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">What tax and regulatory rules apply?</h3>
                <p className="text-sm text-slate-800 mb-4">
                  A summary of tax and regulatory rules that may apply to this property. Figures and exemptions change in budgets and Finance Acts; confirm with your solicitor or accountant before relying on them.
                </p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Council tax band</h4>
                <p className="text-sm text-slate-800 mb-4">
                  The Valuation Office Agency records this property in council tax band <span className="font-semibold">D</span>. The annual amount depends on the local authority's rate, which changes each financial year.
                </p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Stamp Duty Land Tax (SDLT)</h4>
                <p className="text-sm text-slate-700 mb-3">
                  Stamp Duty Land Tax is paid by the buyer on completion. It is charged in bands. The figures below show the standard residential rates that would apply to this property &mdash; first-time-buyer relief and the additional-property surcharge are separate variants you should check with your solicitor.
                </p>
                <div className="border border-slate-200 rounded-sm overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Portion of price</th>
                        <th className="text-left px-4 py-2 font-semibold">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">Up to £250,000</td><td className="px-4 py-2">0%</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">£250,001 to £925,000</td><td className="px-4 py-2">5%</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-2">£925,001 to £1.5m</td><td className="px-4 py-2">10%</td></tr>
                      <tr className="border-t border-slate-200 bg-slate-50"><td className="px-4 py-2">Over £1.5m</td><td className="px-4 py-2">12%</td></tr>
                    </tbody>
                  </table>
                </div>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Section 24 &mdash; landlord mortgage interest</h4>
                <p className="text-sm text-slate-800 mb-4">
                  If this property is bought to let, Section 24 of the Finance (No. 2) Act 2015 restricts the relief individual landlords can claim on mortgage interest. Relief is now given as a basic-rate (20%) tax credit rather than as a deduction from rental income. Owner-occupiers and limited companies are not affected.
                </p>

                <h4 className="text-sm font-semibold text-slate-950 mb-2">Leasehold and Freehold Reform Act 2024</h4>
                <p className="text-sm text-slate-800 mb-4">
                  The Leasehold and Freehold Reform Act 2024 makes it easier for leaseholders to extend leases, buy the freehold, and challenge unfair charges. Some provisions are in force; others come in by secondary legislation. Whether they apply depends on the tenure of the property &mdash; tenure cannot be confirmed without title data, see the Coverage section.
                </p>

                <p className="text-xs text-slate-600">
                  Source: UK statutory rules. SDLT bands HM Treasury / HMRC; Section 24 &mdash; Finance (No. 2) Act 2015; Leasehold and Freehold Reform Act 2024. No external lookup is performed.
                </p>
              </Card>

              {/* CTA */}
              <div className="text-center pt-2">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center font-semibold no-underline transition-colors rounded-lg bg-emerald-700 text-white shadow-sm hover:bg-emerald-800 active:bg-emerald-900 px-4 py-2.5 text-sm min-h-[44px] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#ffdd00] focus-visible:outline-offset-2"
                >
                  Run a Buyer's Report on your property
                </Link>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-slate-200" />

          {/* ─── SELLER'S PACK ─── */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-950">Seller's Pack</h2>
            <p className="mt-2 text-sm text-slate-600">
              You provide the documents and questionnaire answers. We populate TA6, TA7, and LPE1. £79.
            </p>

            <div className="mt-6 space-y-6">
              {/* How it works — process explainer */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-6">How it works</h3>
                <div className="space-y-8">
                  {/* Step 1 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-semibold" aria-hidden="true">1</div>
                    <div>
                      <p className="font-semibold text-slate-950">Confirm your property details</p>
                      <p className="text-sm text-slate-600 mt-1">Enter your address and tenure type. This determines which forms are included in your pack.</p>
                    </div>
                  </div>
                  {/* Step 2 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-semibold" aria-hidden="true">2</div>
                    <div>
                      <p className="font-semibold text-slate-950">Answer the disclosure questionnaire</p>
                      <p className="text-sm text-slate-600 mt-1">
                        Work through the TA6 Property Information Form (59 questions). Leasehold properties also complete the TA7 (26 questions) and LPE1 (22 questions).
                        Free-text answers can optionally be reformatted into the wording your solicitor expects.
                      </p>
                    </div>
                  </div>
                  {/* Step 3 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-semibold" aria-hidden="true">3</div>
                    <div>
                      <p className="font-semibold text-slate-950">Upload your supporting documents</p>
                      <p className="text-sm text-slate-600 mt-1">
                        Upload title register, lease, service charge statements, and other documents. We extract relevant data using OCR and AI to fill in additional form fields.
                      </p>
                    </div>
                  </div>
                  {/* Step 4 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-semibold" aria-hidden="true">4</div>
                    <div>
                      <p className="font-semibold text-slate-950">Review, pay, and download</p>
                      <p className="text-sm text-slate-600 mt-1">
                        Preview your completed forms, check the answers, then pay £79 to generate and download your solicitor-ready PDF bundle.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Before/after: document → form field mapping */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">What extraction looks like</h3>
                <p className="text-sm text-slate-600 mb-4">
                  When you upload a document, we extract the relevant details and map them to the correct form fields. Here are some examples.
                </p>
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">You upload</th>
                        <th className="text-left px-4 py-2 font-semibold">We populate</th>
                        <th className="text-left px-4 py-2 font-semibold hidden sm:table-cell">Example value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {FIELD_MAPPING_EXAMPLES.map((mapping, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'border-t border-slate-200' : 'border-t border-slate-200 bg-slate-50'}>
                          <td className="px-4 py-2 text-slate-800">{mapping.document}</td>
                          <td className="px-4 py-2">
                            <span className="text-slate-950 font-medium">{mapping.field}</span>
                            <span className="text-slate-600 ml-1">({mapping.label})</span>
                          </td>
                          <td className="px-4 py-2 text-slate-600 hidden sm:table-cell">{mapping.example}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-600 mt-3">
                  Your own answers from the disclosure questionnaire always take priority over extracted data. Every populated field shows its source.
                </p>
              </Card>

              {/* Document checklist */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">Document checklist</h3>
                <p className="text-sm text-slate-600 mb-4">
                  These are the documents that can improve your pack. Only the title register and title plan are essential — everything else is optional but helps fill more fields.
                </p>
                <div className="space-y-3">
                  {DOCUMENT_CHECKLIST.map((doc) => (
                    <div key={doc.name} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-0.5 text-base" aria-hidden="true">
                        {doc.required ? '📋' : '📄'}
                      </span>
                      <div>
                        <p className="text-sm text-slate-950">
                          {doc.name}
                          {doc.required && <span className="ml-2 text-xs font-medium text-emerald-700">Required</span>}
                        </p>
                        <p className="text-xs text-slate-600">{doc.why}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* What you get */}
              <Card>
                <h3 className="text-lg font-semibold text-slate-950 mb-4">What you receive</h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-emerald-600 pl-4">
                    <p className="text-sm font-semibold text-slate-950">TA6 Property Information Form</p>
                    <p className="text-xs text-slate-600">59 questions across 14 sections. Required for all property sales.</p>
                  </div>
                  <div className="border-l-4 border-emerald-600 pl-4">
                    <p className="text-sm font-semibold text-slate-950">TA7 Leasehold Information Form</p>
                    <p className="text-xs text-slate-600">26 questions across 7 sections. Included for leasehold properties.</p>
                  </div>
                  <div className="border-l-4 border-emerald-600 pl-4">
                    <p className="text-sm font-semibold text-slate-950">LPE1 Leasehold Property Enquiries</p>
                    <p className="text-xs text-slate-600">22 questions across 7 sections. Included for leasehold properties.</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-4">
                  Each form is generated as a PDF with official section numbering, answer boxes, and provenance annotations showing where each answer came from.
                </p>
              </Card>

              {/* Disclaimer */}
              <Card variant="subtle">
                <p className="text-sm text-slate-700">
                  This is a document preparation tool, not a legal service. You provide the information and documents — we assemble the forms.
                  Review with a qualified solicitor before use. Information and document preparation only. Not legal advice.
                </p>
              </Card>

              {/* CTA */}
              <div className="text-center pt-2">
                <Link
                  to="/selling-documents"
                  className="inline-flex items-center justify-center font-semibold no-underline transition-colors rounded-lg border border-slate-300 bg-white text-slate-950 shadow-sm hover:bg-slate-50 active:bg-slate-100 px-4 py-2.5 text-sm min-h-[44px] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#ffdd00] focus-visible:outline-offset-2"
                >
                  Start your selling documents pack
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
